import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '@principle-theorem/ng-auth';
import { ManagementToolSidebarService } from '@principle-theorem/ng-management-tools';
import { ManagementService } from '@principle-theorem/ng-principle-shared';
import { DialogPresets } from '@principle-theorem/ng-shared';
import {
  HotkeysService as AngularHotkeysService,
  Hotkey,
} from 'angular2-hotkeys';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { SearchComponent } from '../components/toolbar/search/search.component';

interface IAppHotkey {
  hotkey: string | string[];
  label: string;
  actionFn: (event: KeyboardEvent, combo: string) => boolean | undefined;
}

@Injectable({ providedIn: 'root' })
export class HotkeysService {
  private _destroyRef = inject(DestroyRef);
  private _dialog = inject(MatDialog);
  private _hotkeyService = inject(AngularHotkeysService);
  private _managementToolSidebar = inject(ManagementToolSidebarService);
  private _auth = inject(AuthService);
  private _management = inject(ManagementService);
  private _registeredHotkeys: Hotkey[] = [];
  private _cheatSheetHotkey?: Hotkey;

  disable(): void {
    this._hotkeyService.pause(this._hotkeyService.hotkeys);
  }

  enable(): void {
    this._hotkeyService.unpause(this._hotkeyService.pausedHotkeys);
  }

  registerHotkeys(): void {
    const isAuthenticated$ = this._auth.authUser$.pipe(map((user) => !!user));
    const isManagementUser$ = this._management.user$.pipe(
      map((user) => !!user)
    );

    combineLatest([isAuthenticated$, isManagementUser$])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(([isAuthenticated, isManagementUser]) => {
        this._clearRegisteredHotkeys();

        if (isAuthenticated) {
          this._registerHotkey({
            label: 'Open global search',
            hotkey: 's',
            actionFn: (event) => {
              const target = event.target as HTMLElement;
              if (target.nodeName === 'MAT-SELECT') {
                return true;
              }
              this.openSearch();
            },
          });
        }

        if (isManagementUser) {
          this._registerHotkey({
            label: 'Toggle management tools',
            hotkey: 'm',
            actionFn: (event) => {
              const target = event.target as HTMLElement;
              if (target.nodeName === 'MAT-SELECT') {
                return true;
              }
              void this._managementToolSidebar.toggle();
            },
          });
        }

        this._updateCheatSheet();
      });
  }

  openSearch(): void {
    this._dialog.open(SearchComponent, DialogPresets.large({ height: '90vh' }));
  }

  private _clearRegisteredHotkeys(): void {
    for (const hotkey of this._registeredHotkeys) {
      this._hotkeyService.remove(hotkey);
    }
    this._registeredHotkeys = [];
  }

  private _registerHotkey(appHotkey: IAppHotkey): void {
    const { hotkey, label, actionFn } = appHotkey;
    const newHotkey = new Hotkey(
      hotkey,
      (event, combo): boolean => {
        return actionFn(event, combo) ?? false;
      },
      undefined,
      label,
      undefined,
      true
    );
    this._hotkeyService.add(newHotkey);
    this._registeredHotkeys.push(newHotkey);
  }

  private _updateCheatSheet(): void {
    if (this._cheatSheetHotkey) {
      this._hotkeyService.remove(this._cheatSheetHotkey);
      this._cheatSheetHotkey = undefined;
    }

    if (this._registeredHotkeys.length > 0) {
      this._cheatSheetHotkey = new Hotkey(
        '?',
        () => {
          this._hotkeyService.cheatSheetToggle.next();
          return false;
        },
        [],
        'Show / hide this help menu'
      );
      this._hotkeyService.add(this._cheatSheetHotkey);
    }
  }
}
